import { CategoryParent, EMPTY_ARR } from '../../../../constants';
import {
  AccordionRadioGroup,
  Category,
  Option,
  OptionGroup,
  User,
} from '../../../../types';
import { useCategories } from '../../../categories/hooks/useCategories';
import { useUsers } from '../../../middle-office/user/hooks/useUsers';
import { AsyncDataPreset } from '../../constants/AsyncDataPreset';
import { AsyncDataTransformer } from '../../constants/AsyncDataTransformer';
import { AsyncDataConfig } from '../../types/AsyncDataConfig';
import { excludeChildrenByIdentifier } from './logic/excludeChildrenByIdentifier';
import { transform } from './logic/transform';

type TransformedData<T extends AsyncDataTransformer> = {
  [AsyncDataTransformer.Options]: Option<string>[];
  [AsyncDataTransformer.OptionGroups]: OptionGroup<string>[];
  [AsyncDataTransformer.AccordionGroups]: AccordionRadioGroup[];
}[T];

export const useAsyncData = <T extends AsyncDataConfig>(
  config: T | undefined,
  hookOptions?: { enabled?: boolean },
): {
  data: TransformedData<T extends AsyncDataConfig ? T['transformer'] : never>;
  asyncDataAsProps: Record<
    string,
    TransformedData<T extends AsyncDataConfig ? T['transformer'] : never>
  >;
  isLoading: boolean;
} => {
  const isEnabled = !!config && hookOptions?.enabled !== false;
  const isCategories = config?.preset === AsyncDataPreset.Categories;

  const needsChildren =
    config?.transformer === AsyncDataTransformer.OptionGroups ||
    config?.transformer === AsyncDataTransformer.AccordionGroups;

  const { categories = EMPTY_ARR, isLoading: catLoading } = useCategories(
    isCategories && config?.preset === AsyncDataPreset.Categories
      ? config.parent
      : CategoryParent.AssetType,
    { enabled: isEnabled && isCategories, includeChildren: needsChildren },
  );

  const { users = EMPTY_ARR, isLoading: usersLoading } = useUsers(undefined, {
    enabled: isEnabled && !isCategories,
  });

  const filteredCategories =
    isCategories &&
    config?.preset === AsyncDataPreset.Categories &&
    config.exclude?.length
      ? excludeChildrenByIdentifier(categories as Category[], config.exclude)
      : (categories as Category[]);

  const data: TransformedData<
    T extends AsyncDataConfig ? T['transformer'] : never
  > = config
    ? transform(config.transformer, filteredCategories, users as User[])
    : EMPTY_ARR;

  const propKey =
    config?.transformer === AsyncDataTransformer.Options ? 'options' : 'groups';

  return {
    data,
    asyncDataAsProps: { [propKey]: data },
    isLoading: isCategories ? catLoading : usersLoading,
  };
};
